import React, { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";

// Replace these with your own images. Three slides is the sweet spot for a
// "classical" hero slider — enough to feel curated, not a stock carousel.
import heroImage1 from "../../assets/images/Herosection/herobanner.jpg";
import heroImage2 from "../../assets/images/Herosection/herobanner-2.jpeg";
import heroImage3 from "../../assets/images/Herosection/herobanner-3.jpeg";

// ---------------------------------------------------------------------------
// Brand palette — pulled from the Rai & Associates wordmark (ink black,
// parchment, single burgundy accent) rather than the generic black+gold
// "premium law firm" template.
// ---------------------------------------------------------------------------
const INK = "#0E0E0D";
const PARCHMENT = "#F4EFE3";
const BURGUNDY = "#7A1F2B";
const BURGUNDY_LIGHT = "#A8485A";
const STONE = "#B9B2A4";

const DISPLAY_FONT = "'Playfair Display', Georgia, serif";
const SLIDE_DURATION = 6500; // ms between auto-advances

const slides = [
  {
    image: heroImage1,
    eyebrow: "Litigation & Disputes",
    headline: ["Protecting Your", "Rights & Interests"],
    copy: "Rai & Associates provides expert legal guidance with over 20 years of experience — trusted by clients for transparency, dedication, and results.",
  },
  {
    image: heroImage2,
    eyebrow: "Corporate & Advisory",
    headline: ["Counsel Built On", "Integrity & Insight"],
    copy: "From contracts to compliance, we help businesses navigate complex legal terrain with clear, decisive guidance.",
  },
  {
    image: heroImage3,
    eyebrow: "Our Track Record",
    headline: ["Three Decades Of", "Trusted Advocacy"],
    copy: "Hundreds of cases. Thousands of hours in court and counsel. One unwavering commitment to every client we serve.",
  },
];

const HeroSection = () => {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const prefersReducedMotion = useReducedMotion();
  const timerRef = useRef(null);

  const goTo = useCallback((i) => {
    setIndex(((i % slides.length) + slides.length) % slides.length);
  }, []);

  const next = useCallback(() => goTo(index + 1), [goTo, index]);
  const prev = useCallback(() => goTo(index - 1), [goTo, index]);

  // Autoplay, paused on hover and respecting reduced-motion preference
  useEffect(() => {
    if (paused || prefersReducedMotion) return undefined;
    timerRef.current = setTimeout(next, SLIDE_DURATION);
    return () => clearTimeout(timerRef.current);
  }, [index, paused, prefersReducedMotion, next]);

  const slide = slides[index];

  return (
    <section
      className="relative h-[68vh] min-h-[380px] max-h-[560px] w-full overflow-hidden text-white sm:h-[75vh] sm:min-h-[460px] sm:max-h-[640px] lg:h-[85vh] lg:min-h-[560px] lg:max-h-[820px]"
      style={{ backgroundColor: INK }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* ---- Slide imagery, crossfade + slow Ken Burns drift ---- */}
      <AnimatePresence mode="sync">
        <motion.div
          key={slide.image}
          className="absolute inset-0"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 1.1, ease: [0.43, 0.13, 0.23, 0.96] }}
        >
          <motion.img
            src={slide.image}
            alt=""
            className="h-full w-full object-cover"
            initial={{ scale: 1 }}
            animate={{ scale: prefersReducedMotion ? 1 : 1.08 }}
            transition={{
              duration: SLIDE_DURATION / 1000 + 1.5,
              ease: "linear",
            }}
          />
          <div
            className="absolute inset-0"
            style={{
              background:
                "linear-gradient(180deg, rgba(14,14,13,0.55) 0%, rgba(14,14,13,0.74) 55%, rgba(14,14,13,0.94) 100%)",
            }}
          />
        </motion.div>
      </AnimatePresence>

      {/* ---- Watermark, the wordmark's ampersand ghosted large ---- */}
      {/* <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-[6vw] top-1/2 -translate-y-1/2 select-none text-[60vw] sm:text-[52vw] lg:text-[44vw]"
        style={{
          fontFamily: DISPLAY_FONT,
          lineHeight: 1,
          color: PARCHMENT,
          opacity: 0.045,
        }}
      >
        Rai &
      </div> */}

      {/* ---- Copy block ---- */}
      <div className="relative z-10 mx-auto flex h-full max-w-7xl flex-col justify-center px-5 pb-14 sm:px-8 sm:pb-16 lg:px-12 lg:pb-20">
        <AnimatePresence mode="wait">
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="max-w-2xl"
          >
            <div className="mb-3 flex items-center gap-3 sm:mb-5 sm:gap-4">
              <span
                className="h-px w-8 sm:w-10"
                style={{ backgroundColor: BURGUNDY_LIGHT }}
              />
              <span
                className="text-[10px] font-semibold uppercase tracking-[0.3em] sm:text-xs sm:tracking-[0.35em]"
                style={{ color: BURGUNDY_LIGHT }}
              >
                {slide.eyebrow}
              </span>
            </div>

            <h1
              className="mb-3 text-2xl font-bold uppercase leading-[1.1] sm:mb-5 sm:text-4xl md:text-5xl lg:mb-6 lg:text-6xl"
              style={{ fontFamily: DISPLAY_FONT }}
            >
              {slide.headline[0]} <br />
              <span style={{ color: BURGUNDY_LIGHT }}>
                {slide.headline[1]}
              </span>
            </h1>

            <p
              className="mb-5 max-w-xl text-sm sm:mb-8 sm:text-base md:mb-10 md:text-lg"
              style={{ color: STONE }}
            >
              {slide.copy}
            </p>
          </motion.div>
        </AnimatePresence>

        <div className="flex flex-col gap-3 sm:flex-row sm:gap-4">
          <a
            href="/contact-us"
            className="group relative inline-block overflow-hidden border px-6 py-2.5 text-center text-sm font-semibold uppercase tracking-widest sm:px-8 sm:py-3 sm:text-base"
            style={{ borderColor: BURGUNDY_LIGHT, color: PARCHMENT }}
          >
            <span className="relative z-10 transition-colors duration-300 group-hover:text-white">
              Schedule Consultation
            </span>
            <span
              className="absolute inset-0 -z-0 translate-y-full transition-transform duration-500 ease-out group-hover:translate-y-0"
              style={{ backgroundColor: BURGUNDY }}
            />
          </a>

          <a
            href="/services"
            className="border border-white/20 px-6 py-2.5 text-center text-sm font-semibold uppercase tracking-widest transition-all duration-300 hover:border-white/60 sm:px-8 sm:py-3 sm:text-base"
          >
            Practice Areas
          </a>
        </div>
      </div>

      {/* ---- Classical slide navigator: numbered tabs + progress line ---- */}
      <div className="absolute inset-x-0 bottom-0 z-10 border-t border-white/10">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-3 sm:px-8 sm:py-4 lg:px-12 lg:py-5">
          <div className="flex items-center gap-3 sm:gap-6">
            {slides.map((s, i) => (
              <button
                key={s.image}
                type="button"
                onClick={() => goTo(i)}
                className="group flex items-center gap-2"
                aria-label={`Go to slide ${i + 1}`}
                aria-current={i === index}
              >
                <span
                  className="text-xs tabular-nums transition-colors duration-300 sm:text-sm"
                  style={{
                    fontFamily: DISPLAY_FONT,
                    color:
                      i === index ? PARCHMENT : "rgba(255,255,255,0.35)",
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span
                  className="h-px w-4 transition-all duration-500 sm:w-6"
                  style={{
                    backgroundColor:
                      i === index ? BURGUNDY_LIGHT : "rgba(255,255,255,0.2)",
                  }}
                />
              </button>
            ))}
          </div>
{/* 
          <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.25em] text-white/50 sm:gap-6 sm:text-xs sm:tracking-[0.3em]">
            <button
              type="button"
              onClick={prev}
              className="transition-colors hover:text-white"
            >
              Prev
            </button>
            <span className="text-white/20">/</span>
            <button
              type="button"
              onClick={next}
              className="transition-colors hover:text-white"
            >
              Next
            </button>
          </div> */}
        </div>

        <div className="h-px w-full bg-white/10">
          {!paused && !prefersReducedMotion && (
            <motion.div
              key={index}
              className="h-full"
              style={{ backgroundColor: BURGUNDY_LIGHT }}
              initial={{ width: "0%" }}
              animate={{ width: "100%" }}
              transition={{ duration: SLIDE_DURATION / 1000, ease: "linear" }}
            />
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;